import axios from 'axios';

// Define the base API URL
// In production, API requests will use the relative path which will be handled by Apache proxy
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:5050/api');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor to add auth token
api.interceptors.request.use((config: any) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (userData: any) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  }
};

// Gallery API
export const galleryAPI = {
  getImages: async (filters: any = {}) => {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.featured) params.append('featured', 'true');
    
    // Always add a cache-busting timestamp
    params.append('_t', (Date.now() + Math.random()).toString());
    
    // Log the actual API request being made
    console.log(`Making API request to: /gallery?${params.toString()}`);
    
    // Use a custom config to bypass any caching
    const response = await api.get(`/gallery?${params.toString()}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    console.log('Gallery API response:', {
      count: response.data.count,
      featured: response.data.data.filter((img: any) => img.featured).length > 0
    });
    
    return response.data;
  },
  getImage: async (id: string) => {
    const response = await api.get(`/gallery/${id}`);
    return response.data;
  },
  uploadImage: async (formData: FormData) => {
    const response = await api.post('/gallery', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  updateImage: async (id: string, data: any) => {
    console.log('API updateImage called with:', { id, data });
    const response = await api.patch(`/gallery/${id}`, data, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    return response.data;
  },
  
  updateFeatured: async (id: string, featured: boolean) => {
    console.log('API updateFeatured called:', { id, featured });
    try {
      // First try the dedicated featured endpoint
      const response = await api.patch(`/gallery/${id}/featured`, { 
        featured: featured 
      }, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return response.data;
    } catch (error: any) {
      console.log('Featured endpoint failed, falling back to regular update endpoint', error);
      
      // If the dedicated endpoint fails (404), fall back to the regular update endpoint
      const fallbackResponse = await api.patch(`/gallery/${id}`, { 
        featured: featured 
      }, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return fallbackResponse.data;
    }
  },
  updateImageWithFile: async (id: string, formData: FormData) => {
    // When sending FormData, axios will automatically set the correct Content-Type with boundary
    // Explicitly setting 'Content-Type': 'multipart/form-data' can cause issues with boundary
    const response = await api.put(`/gallery/${id}`, formData, {
      headers: {
        // Remove the Content-Type header to let axios set it with the proper boundary
      }
    });
    return response.data;
  },
  deleteImage: async (id: string) => {
    const response = await api.delete(`/gallery/${id}`);
    return response.data;
  }
};

// Profile API
export interface SocialLink {
  platform: string;
  url: string;
  id?: string;
}

export interface ContactInfo {
  email: string;
  phone?: string;
  address?: string;
}

export interface ProfileData {
  _id?: string;
  bio: string;
  socialLinks: SocialLink[];
  contactInfo: ContactInfo;
  userId?: string;
  updatedAt?: string;
}

export const profileAPI = {
  getProfile: async () => {
    try {
      console.log('Fetching profile...');
      const response = await api.get('/profile');
      console.log('Raw API response:', response);
      
      // Return the data property from the response
      return response.data;
    } catch (error: any) {
      console.error('API Error - getProfile:', error);
      if (error?.response) {
        console.error('Response details:', error.response.data, error.response.status);
      }
      throw error;
    }
  },
  updateProfile: async (data: Partial<ProfileData>) => {
    try {
      const response = await api.patch('/profile', data);
      return response.data;
    } catch (error: any) {
      console.error('API Error - updateProfile:', error);
      if (error?.response) {
        console.error('Response details:', error.response.data, error.response.status);
      }
      throw error;
    }
  }
};

// Testimonials API
export interface Testimonial {
  id?: string;
  name: string;
  role?: string;
  content: string;
  rating?: number;
  date?: Date;
  approved?: boolean;
}

export const testimonialAPI = {
  getTestimonials: async (onlyApproved = true) => {
    const params = new URLSearchParams();
    if (onlyApproved) params.append('approved', 'true');
    
    // Add cache-busting parameter like in galleryAPI
    params.append('_t', (Date.now() + Math.random()).toString());
    
    console.log(`Making testimonial API request to: /testimonials?${params.toString()}`);
    
    const response = await api.get(`/testimonials?${params.toString()}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    // Return the data property directly to match other API methods
    return response.data;
  },
  getTestimonial: async (id: string) => {
    const response = await api.get(`/testimonials/${id}`);
    return response.data;
  },
  createTestimonial: async (data: Testimonial) => {
    const response = await api.post('/testimonials', data);
    return response.data;
  },
  updateTestimonial: async (id: string, data: Partial<Testimonial>) => {
    const response = await api.patch(`/testimonials/${id}`, data);
    return response.data;
  },
  deleteTestimonial: async (id: string) => {
    const response = await api.delete(`/testimonials/${id}`);
    return response.data;
  },
  approveTestimonial: async (id: string) => {
    try {
      console.log(`Attempting to approve testimonial with ID: ${id}`);
      
      // First try the dedicated approval endpoint
      const response = await api.patch(`/testimonials/${id}/approve`, { approved: true }, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      console.log('Approval endpoint succeeded:', response);
      return response.data;
    } catch (error) {
      console.error('Approval endpoint failed, trying regular update endpoint', error);
      
      // Fall back to regular update if the dedicated endpoint fails
      const fallbackResponse = await api.patch(`/testimonials/${id}`, { approved: true }, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      console.log('Fallback update endpoint succeeded:', fallbackResponse);
      return fallbackResponse.data;
    }
  }
};

// Booking API
export interface AvailabilitySlot {
  id: string;
  startTime: string;
  endTime: string;
  isBooked: boolean;
}

export interface BookingFormData {
  name: string;
  email: string;
  phone: string;
  message?: string;
  availabilityId: string;
}

export interface CreateAvailabilityData {
  startTime: string;
  endTime: string;
}

export const fetchAvailability = async () => {
  const response = await api.get('/booking/availability');
  return response.data;
};

export const createAvailabilitySlot = async (data: CreateAvailabilityData) => {
  const response = await api.post('/booking/availability', data);
  return response.data;
};

export const deleteAvailabilitySlot = async (id: string) => {
  const response = await api.delete(`/booking/availability/${id}`);
  return response.data;
};

export const createBooking = async (data: BookingFormData) => {
  const response = await api.post('/booking', data);
  return response.data;
};

export const getBookings = async () => {
  const response = await api.get('/booking');
  return response.data;
};

export const updateBookingStatus = async (id: string, status: string) => {
  const response = await api.patch(`/booking/${id}/status`, { status });
  return response.data;
};

export default api;